import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import InfoIcon from '@material-ui/icons/Info';
import { Box, Grid, Button, Container, Typography, Tooltip } from '@material-ui/core';
import i18n from 'i18n-js';
import useStyles from './Services.styled';
import { uiActions } from 'state/modules/UI';
import { SelectableList } from 'components/UI';
import { vehicleActions } from 'state/modules/vehicles';
import { useConfirm } from 'hooks';
import moment from 'moment';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { UnshareDisclaimer } from '../../../VehiclesGroups';

const ServiceRowRenderer = ({ row }) => {
  const localeFormat = useSelector((state) => state.organizations?.localeFormat);
  const classes = useStyles();
  return (
    <div className={classes.cellRender} data-tag="allowRowEvents">
      {row?.name}
      {row?.status === 'BLOCKED' || row?.status === 'UNSHARING' ? (
        <>
          <span
            className={clsx(!row.selected && classes.warning, classes.disclaimer)}
            data-tag="allowRowEvents"
          >
            {i18n.t('vehicle_groups.on_unshare')}{' '}
          </span>
          <Tooltip
            className={classes.tooltip}
            title={
              <>
                <span className={classes.tooltipTitle}>
                  {i18n.t('vehicle_groups.unshare_tooltip_title')}
                </span>
                <br />
                <span>{i18n.t('vehicle_groups.unshare_tooltip_msg')}</span>
              </>
            }
            placement="right"
          >
            <InfoIcon className={clsx(!row.selected && classes.tooltipColor, classes.infoIcon)} />
          </Tooltip>
        </>
      ) : null}
      {row.future ? (
        <span
          className={clsx(!row.selected && classes.warning, classes.future)}
          data-tag="allowRowEvents"
        >
          {i18n.t('vehicle_groups.future_disclaimer', {
            startTime: moment(row?.startTime).format(localeFormat),
          })}
        </span>
      ) : null}
    </div>
  );
};

const VehiclesServices = ({ currentVehicle, step, loading, showLabel = true, orgs }) => {
  const vehicle_group =
    currentVehicle.vehicle_group ||
    currentVehicle.customer_vehicle_group ||
    currentVehicle.owner_vehicle_group;
  const classes = useStyles();
  const dispatch = useDispatch();
  const [assignedServices, setAssignedServices] = useState([]);
  const [availablesServices, setAvailablesServices] = useState([]);
  const [selectedServiceRow, setSelectedAvailablesRow] = useState(null);
  const [isAssignedSelected, setAssignedSelected] = useState(false);
  const [isAvailableSelected, setAvailableSelected] = useState(false);
  const orgGracePeriod = useSelector(
    (state) => state.config?.settings?.core?.organization?.share_grace_period
  );

  const availablesServicesColumns = [
    {
      name: i18n.t('common.name'),
      selector: 'name',
      cell: (row) => <ServiceRowRenderer row={row} />,
      sortable: false,
    },
  ];

  const assignedServicesColumns = [
    {
      name: i18n.t('common.name'),
      selector: 'name',
      cell: (row) => <ServiceRowRenderer row={row} />,
      sortable: false,
    },
  ];

  useEffect(() => {
    if (vehicle_group.id) {
      dispatch(
        vehicleActions.getAvailablesServices({
          status: ['ACTIVE', 'CANREQUESTED'],
          vehicle_group_id: vehicle_group.id,
        })
      );
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (orgs?.length > 0) {
      setAssignedServices(parseAssignedServices(orgs));
    }

    if (step?.availables?.list?.length > 0) {
      setAvailablesServices(filteredServices() || []);
    }

    setSelectedAvailablesRow(null);
  }, [step, currentVehicle, orgs]); // eslint-disable-line

  /**
   *
   * @returns list of assigned services
   */

  const parseAssignedServices = (services = []) =>
    services
      .filter(
        (service) =>
          !service?.is_owner && service?.status !== 'BLOCKED' && service?.status !== 'UNSHARING'
      )
      .map((service) => {
        const future =
          service?.status === 'ACTIVE' && moment(service?.start_time).isAfter(new Date());
        return {
          ...service.organization,
          gracePeriod: service.relationship?.share_grace_period,
          startTime: service.start_time,
          future,
          disabled: future,
        };
      });

  /**
   *
   * @returns list of available services
   */

  const filteredServices = () => {
    const services = [...(step?.availables?.list || [])];
    const assignedServices = [...(orgs || [])];

    const filteredArray = services
      .filter(
        (service) =>
          !orgs.filter((org) => org.status === 'ACTIVE' && org?.organization?.id === service?.id)
            .length > 0
      )
      .map((service) => {
        if (assignedServices?.length === 0) {
          return service;
        } else {
          for (let i = 0; i < assignedServices.length; i++) {
            if (assignedServices[i]?.organization?.id === service?.id) {
              service.status = assignedServices[i].status;
              if (
                assignedServices[i].status !== 'BLOCKED' &&
                assignedServices[i].status !== 'UNSHARING'
              ) {
                service.disabled = true;
              }
            } else {
              service.disabled = false;
            }
          }
          return service;
        }
      });

    return filteredArray;
  };

  const onAvailablesRowClicked = (row) => {
    setSelectedAvailablesRow(row?.id);
  };

  const gracePeriod =
    assignedServices.find((service) => service.id === selectedServiceRow)?.gracePeriod ||
    orgGracePeriod ||
    0;
  const orgToShare = availablesServices.find((service) => service.id === selectedServiceRow);

  const addServiceToVehicle = () => {
    if (!selectedServiceRow) {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: i18n.t('vehicle.select_service_before_add_to_service_area'),
        })
      );
    }
    const payload = {
      organization_id: selectedServiceRow,
    };
    orgToShare.selected = false;
    setAvailableSelected(false);
    dispatch(vehicleActions.fetchAddServicesToVehicles(currentVehicle.id, payload));
  };

  const removeServiceToVehicle = () => {
    if (!selectedServiceRow) {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: i18n.t('vehicle.select_service_before_remove_from_service_area'),
        })
      );
    }

    setAssignedSelected(false);
    toggleUnshareVehicle();
  };

  const [toggleUnshareVehicle, UnshareVehicleConfirm] = useConfirm({
    title: i18n.t('vehicle.bulk_unshare_confirm_title'),
    message: <UnshareDisclaimer gracePeriod={gracePeriod} />,
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.yes'),
    onConfirm: () =>
      dispatch(vehicleActions.fetchRemoveServicesToVehicles(currentVehicle.id, selectedServiceRow)),
    onCancel: () => setAssignedSelected(true),
    additionalProps: { modalProps: { maxWidth: 450, containerCustomClass: classes.confirmHeader } },
  });

  const [toggleShareVehicle, ShareVehicleConfirm] = useConfirm({
    title: i18n.t('vehicle.share_unsharing_title'),
    message: i18n.t('vehicle.share_unsharing_msg'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.share'),
    onConfirm: addServiceToVehicle,
    onCancel: () => {
      setAvailableSelected(false);
      setSelectedAvailablesRow(null);
      orgToShare.selected = false;
    },
  });

  return (
    <Box className={classes.container}>
      <UnshareVehicleConfirm />
      <ShareVehicleConfirm />
      <Container className="h-100">
        <Grid container direction="row" justifyContent="center" alignItems="center">
          <Grid item xs={12}>
            <Typography gutterBottom component="div">
              <Box fontWeight="fontWeightBold" fontSize={14} mb={4}>
                {showLabel ? i18n.t('vehicle_groups.grant_access_to_services') : ''}
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Grid container direction="row" justifyContent="space-around" alignItems="flex-start">
              <Grid item xs={5}>
                <SelectableList
                  mainTable={true}
                  pagination={false}
                  title="Available Services"
                  columns={availablesServicesColumns}
                  loading={step?.availables?.loading}
                  data={[...availablesServices] || []}
                  onRowClicked={(row) => {
                    setAvailableSelected(!!row);
                    onAvailablesRowClicked(row);
                  }}
                />
              </Grid>
              <Grid item xs={2}>
                <Box
                  display="flex"
                  alignItems="center"
                  flexDirection="column"
                  justifyContent="center"
                >
                  <Box py={1} px={2} style={{ width: '100%' }}>
                    <Button
                      onClick={
                        orgToShare?.status === 'BLOCKED' || orgToShare?.status === 'UNSHARING'
                          ? toggleShareVehicle
                          : addServiceToVehicle
                      }
                      disabled={loading || !isAvailableSelected}
                      data-cy="add-service"
                      variant="outlined"
                      color="primary"
                      fullWidth
                    >
                      {i18n.t('common.add')}
                    </Button>
                  </Box>
                  <Box py={1} px={2} style={{ width: '100%' }}>
                    <Button
                      onClick={removeServiceToVehicle}
                      disabled={loading || !isAssignedSelected}
                      variant="outlined"
                      color="primary"
                      fullWidth
                    >
                      {i18n.t('common.remove')}
                    </Button>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={5}>
                <Box mb={5}>
                  <SelectableList
                    loading={loading}
                    pagination={false}
                    data={assignedServices || []}
                    title={i18n.t('vehicle.vehicle_services')}
                    columns={assignedServicesColumns}
                    onRowClicked={(row) => {
                      setAssignedSelected(!!row);
                      onAvailablesRowClicked(row);
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default VehiclesServices;
